import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { Button, Card, ErrorNotice } from "../../components/form.js";
import {
  currentSubscription,
  disablePush,
  enablePush,
  PushPermissionError,
  pushSupport,
} from "../../push.js";

/** Turn new-mail notifications on or off for this browser, and send yourself a test. */
export function NotificationsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const config = useQuery({ queryKey: ["push", "config"], queryFn: () => api.push.config() });
  const devices = useQuery({ queryKey: ["push", "devices"], queryFn: () => api.push.devices() });
  const local = useQuery({
    queryKey: ["push", "local"],
    queryFn: async () => ({
      support: await pushSupport(),
      subscribed: Boolean(await currentSubscription()),
      permission: "Notification" in window ? Notification.permission : "denied",
    }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["push"] });
  const enable = useMutation({
    mutationFn: () => enablePush(config.data?.publicKey ?? ""),
    onSettled: refresh,
  });
  const disable = useMutation({ mutationFn: () => disablePush(), onSettled: refresh });
  const test = useMutation({ mutationFn: () => api.push.test() });

  const reason = !config.data?.enabled
    ? t("settings.notifications.serverOff")
    : local.data?.support === "unsupported"
      ? t("settings.notifications.unsupported")
      : local.data?.support === "no-worker"
        ? t("settings.notifications.noWorker")
        : local.data?.permission === "denied"
          ? t("settings.notifications.blocked")
          : null;
  const on = local.data?.subscribed ?? false;
  const permissionError =
    enable.error instanceof PushPermissionError ? t("settings.notifications.blocked") : null;

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 md:p-8">
      <h1 className="text-[22px] text-ink">{t("nav.notifications")}</h1>
      <Card title={t("settings.notifications.title")}>
        <p className="text-sm text-ink-2">{t("settings.notifications.intro")}</p>
        {reason ? (
          <p className="rounded-md bg-surface-2 px-3 py-2 text-sm text-ink-2">{reason}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">
              {on ? t("settings.notifications.on") : t("settings.notifications.off")}
            </span>
            {on ? (
              <>
                <Button
                  variant="secondary"
                  disabled={disable.isPending}
                  onClick={() => disable.mutate()}
                >
                  {t("settings.notifications.turnOff")}
                </Button>
                <Button variant="secondary" disabled={test.isPending} onClick={() => test.mutate()}>
                  {t("settings.notifications.sendTest")}
                </Button>
              </>
            ) : (
              <Button disabled={enable.isPending} onClick={() => enable.mutate()}>
                {t("settings.notifications.turnOn")}
              </Button>
            )}
          </div>
        )}
        {test.data ? (
          <p className="text-sm text-ink-2">
            {t("settings.notifications.testSent", { count: test.data.sent })}
          </p>
        ) : null}
        {permissionError ? <p className="text-sm text-danger">{permissionError}</p> : null}
        <ErrorNotice
          error={permissionError ? null : (enable.error ?? disable.error ?? test.error)}
        />
        <p className="text-xs text-ink-3">
          {t("settings.notifications.devices", { count: devices.data?.devices ?? 0 })} ·{" "}
          {t("settings.notifications.privacy")}
        </p>
      </Card>
    </section>
  );
}
